import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Eyebrow, Reveal } from "./bits";
import { scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];
const LOOP_MS = 55000;

const services = [
  {
    slug: "mutual-funds",
    title: "Mutual Funds",
    category: "Investing",
    tint: "#DDF7EC",
    desc: "Curated mutual fund solutions aligned with your goals, time horizon and risk profile.",
    art: "arcs",
    body: [
      "As an AMFI-registered mutual fund distributor, we help you invest across equity, debt and hybrid funds from India's leading fund houses — chosen for your goals, not for commissions.",
      "Whether it's a monthly SIP or a one-time investment, every recommendation is mapped to a time horizon and a risk profile you understand and are comfortable with.",
      "We review your portfolio with you periodically and rebalance when life or markets shift.",
    ],
    fine: "Mutual fund investments are subject to market risks. Read all scheme related documents carefully.",
  },
  {
    slug: "insurance",
    title: "Insurance",
    category: "Protection",
    tint: "#FBE5F1",
    desc: "Term and health protection that safeguards your family and your goals.",
    art: "shield",
    body: [
      "Protection comes before growth. A single uninsured event can undo years of disciplined investing — insurance is the foundation every financial plan stands on.",
      "We help you estimate the right life and health cover for your situation, keeping premiums efficient and steering clear of investment-bundled policies that dilute both.",
    ],
    fine: "Insurance solutions are offered through licensed partners. Please read all policy documents carefully.",
  },
  {
    slug: "stock-broking",
    title: "Stock Broking",
    category: "Investing",
    tint: "#DDF4FC",
    desc: "Direct access to equities through trusted broking platforms, for informed investors.",
    art: "candles",
    body: [
      "For investors who want to own individual stocks, we facilitate access to reliable, low-cost broking platforms with a smooth account-opening experience.",
      "More importantly, we help you size direct equity exposure sensibly within your overall allocation — so stock picking never endangers the goals that matter.",
    ],
    fine: "Investments in the securities market are subject to market risks. Offered through partner broking platforms.",
  },
  {
    slug: "fixed-income",
    title: "Fixed Income Products",
    category: "Income",
    tint: "#E8EFF4",
    desc: "Bonds, deposits and debt instruments for stability and predictable income.",
    art: "layers",
    body: [
      "Fixed income brings ballast to a portfolio — corporate bonds, NCDs, government securities and deposits that pay defined, predictable returns.",
      "We use laddering and credit-quality filters to balance yield with safety, especially for near-term goals and retirement income.",
    ],
    fine: "Returns are indicative and subject to issuer credit risk. Read offer documents carefully.",
  },
  {
    slug: "pms-aif",
    title: "PMS / AIF",
    category: "Managed",
    tint: "#FFF7C7",
    desc: "Portfolio management and alternative funds for larger, sophisticated portfolios.",
    art: "gem",
    body: [
      "For portfolios above ₹50 lakh (PMS) and ₹1 crore (AIF), we curate professional portfolio management and alternative investment strategies — long-only equity, long-short and structured approaches.",
      "These suit investors seeking concentrated, actively managed strategies beyond mutual funds. We always start with a clear conversation on risks, liquidity and fees.",
    ],
    fine: "Minimum ticket sizes apply as per SEBI regulations. Higher-risk products — suitability is assessed first.",
  },
  {
    slug: "smallcase",
    title: "Smallcase",
    category: "Investing",
    tint: "#DDF7EC",
    desc: "Theme-based model portfolios of stocks and ETFs that you own directly.",
    art: "basket",
    body: [
      "Smallcases are ready-made baskets of stocks or ETFs built around an idea — from dividends to electric mobility — held directly in your own demat account.",
      "Transparent, low-cost and easy to rebalance, they work well as a satellite allocation around a mutual fund core.",
    ],
    fine: "Offered through partner platforms. Investments are subject to market risks.",
  },
  {
    slug: "loans",
    title: "Loans",
    category: "Credit",
    tint: "#FFF7C7",
    desc: "Home loans, loans against securities and business loans through lending partners.",
    art: "key",
    body: [
      "Borrowing is part of the financial plan too. We help you access home loans, loans against your investments, and business credit through trusted lending partners.",
      "A loan against securities can fund an immediate need without breaking your compounding — we help you weigh the trade-offs clearly before you decide.",
    ],
    fine: "Loans are subject to lender approval, eligibility and terms. Facilitated through lending partners.",
  },
  {
    slug: "financial-planning",
    title: "Financial Planning",
    category: "Advisory",
    tint: "#DDF4FC",
    desc: "A structured approach to understanding where you are and where you want to go.",
    art: "grid",
    body: [
      "A structured look at your complete picture — income, expenses, assets, liabilities, responsibilities and dreams.",
      "The output is a written plan: what to do, in what order, and by when. It becomes the reference point for every money decision that follows.",
    ],
    fine: "Planning is advisory in nature and reviewed with you as life changes.",
  },
  {
    slug: "wealth-management",
    title: "Wealth Management",
    category: "Advisory",
    tint: "#E8EFF4",
    desc: "A long-term approach to building, managing and preserving wealth.",
    art: "bars",
    body: [
      "As wealth grows, so does complexity — multiple goals, tax considerations, succession and legacy questions.",
      "We bring it all together into one long-term strategy: building, managing and eventually transferring wealth with intention.",
    ],
    fine: "Suitability and taxation are discussed before any recommendation.",
  },
];

const Art = ({ kind }) => (
  <svg
    viewBox="0 0 96 96"
    className="h-24 w-24 transition-transform duration-700 ease-out group-hover:-rotate-6 group-hover:scale-110"
    fill="none"
    aria-hidden="true"
  >
    {kind === "arcs" && (
      <>
        <path d="M12 84 A 72 72 0 0 1 84 12" stroke="rgba(16,32,46,0.14)" strokeWidth="1.2" />
        <path d="M24 84 A 60 60 0 0 1 84 24" stroke="rgba(5,150,105,0.4)" strokeWidth="1.2" />
        <path d="M38 84 A 46 46 0 0 1 84 38" stroke="rgba(5,150,105,0.75)" strokeWidth="1.6" />
        <circle cx="84" cy="38" r="3" fill="#D9A420" />
      </>
    )}
    {kind === "grid" && (
      <>
        {[28, 48, 68].map((p) => (
          <g key={p} stroke="rgba(16,32,46,0.10)" strokeWidth="1">
            <line x1={p} y1="14" x2={p} y2="82" />
            <line x1="14" y1={p} x2="82" y2={p} />
          </g>
        ))}
        <path d="M16 76 C 36 70, 52 48, 80 22" stroke="rgba(5,150,105,0.75)" strokeWidth="1.6" />
        <circle cx="80" cy="22" r="3.5" fill="#D9A420" />
      </>
    )}
    {kind === "target" && (
      <>
        <circle cx="48" cy="48" r="36" stroke="rgba(16,32,46,0.12)" strokeWidth="1.2" />
        <circle cx="48" cy="48" r="24" stroke="rgba(5,150,105,0.35)" strokeWidth="1.2" />
        <circle cx="48" cy="48" r="12" stroke="rgba(5,150,105,0.65)" strokeWidth="1.4" />
        <circle cx="48" cy="48" r="3.5" fill="#D9A420" />
      </>
    )}
    {kind === "bars" && (
      <>
        <line x1="12" y1="82" x2="84" y2="82" stroke="rgba(16,32,46,0.14)" strokeWidth="1.2" />
        <rect x="20" y="58" width="9" height="24" rx="2" fill="rgba(16,32,46,0.10)" />
        <rect x="36" y="46" width="9" height="36" rx="2" fill="rgba(5,150,105,0.3)" />
        <rect x="52" y="32" width="9" height="50" rx="2" fill="rgba(5,150,105,0.5)" />
        <rect x="68" y="18" width="9" height="64" rx="2" fill="rgba(5,150,105,0.8)" />
      </>
    )}
    {kind === "shield" && (
      <>
        <path
          d="M48 12 L78 22 V48 C78 68 64 80 48 86 C32 80 18 68 18 48 V22 Z"
          stroke="rgba(5,150,105,0.65)"
          strokeWidth="1.6"
        />
        <path d="M38 48 L46 56 L60 40" stroke="#D9A420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
    {kind === "candles" && (
      <>
        <line x1="24" y1="20" x2="24" y2="44" stroke="rgba(5,150,105,0.5)" strokeWidth="1.4" />
        <rect x="19" y="26" width="10" height="14" rx="2" fill="rgba(5,150,105,0.45)" />
        <line x1="48" y1="14" x2="48" y2="52" stroke="rgba(16,32,46,0.25)" strokeWidth="1.4" />
        <rect x="43" y="30" width="10" height="16" rx="2" fill="rgba(16,32,46,0.15)" />
        <line x1="72" y1="26" x2="72" y2="74" stroke="rgba(5,150,105,0.7)" strokeWidth="1.4" />
        <rect x="67" y="38" width="10" height="22" rx="2" fill="rgba(5,150,105,0.75)" />
        <circle cx="72" cy="22" r="3" fill="#D9A420" />
      </>
    )}
    {kind === "layers" && (
      <>
        <line x1="16" y1="30" x2="80" y2="30" stroke="rgba(16,32,46,0.14)" strokeWidth="2" strokeLinecap="round" />
        <line x1="16" y1="46" x2="80" y2="46" stroke="rgba(5,150,105,0.35)" strokeWidth="2" strokeLinecap="round" />
        <line x1="16" y1="62" x2="80" y2="62" stroke="rgba(5,150,105,0.6)" strokeWidth="2" strokeLinecap="round" />
        <line x1="16" y1="78" x2="64" y2="78" stroke="rgba(5,150,105,0.85)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="72" cy="78" r="3.5" fill="#D9A420" />
      </>
    )}
    {kind === "gem" && (
      <>
        <path d="M30 30 L66 30 L48 74 Z" stroke="rgba(5,150,105,0.7)" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M30 30 L48 46 L66 30" stroke="rgba(16,32,46,0.18)" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M48 46 L48 74" stroke="rgba(16,32,46,0.18)" strokeWidth="1.2" />
        <circle cx="48" cy="20" r="3.5" fill="#D9A420" />
      </>
    )}
    {kind === "basket" && (
      <>
        <rect x="18" y="18" width="26" height="26" rx="6" stroke="rgba(5,150,105,0.75)" strokeWidth="1.6" />
        <rect x="52" y="18" width="26" height="26" rx="6" stroke="rgba(16,32,46,0.16)" strokeWidth="1.6" />
        <rect x="18" y="52" width="26" height="26" rx="6" stroke="rgba(16,32,46,0.16)" strokeWidth="1.6" />
        <rect x="52" y="52" width="26" height="26" rx="6" stroke="rgba(5,150,105,0.4)" strokeWidth="1.6" />
        <circle cx="65" cy="31" r="3" fill="#D9A420" />
      </>
    )}
    {kind === "key" && (
      <>
        <circle cx="34" cy="34" r="16" stroke="rgba(5,150,105,0.7)" strokeWidth="1.8" />
        <line x1="46" y1="46" x2="74" y2="74" stroke="rgba(5,150,105,0.7)" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="62" y1="62" x2="72" y2="56" stroke="rgba(16,32,46,0.3)" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="70" y1="70" x2="80" y2="64" stroke="rgba(16,32,46,0.3)" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="34" cy="34" r="4" fill="#D9A420" />
      </>
    )}
  </svg>
);

export const ServiceMarquee = () => {
  const [open, setOpen] = useState(null);
  const active = services.find((s) => s.slug === open);
  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const st = useRef({ offset: 0, hover: false, focus: false, dragging: false, armed: false, moved: 0, lastX: 0, pointerId: null });

  const apply = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const W = track.scrollWidth / 2;
    if (W <= 0) return;
    let o = st.current.offset % W;
    if (o > 0) o -= W;
    st.current.offset = o;
    track.style.transform = `translate3d(${o}px, 0, 0)`;
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const mqMobile = window.matchMedia("(max-width: 1023px)");
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let raf;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(now - last, 64);
      last = now;
      const s = st.current;
      if (!s.hover && !s.focus && !s.dragging) {
        const W = track.scrollWidth / 2;
        if (W > 0) {
          s.offset -= (W / LOOP_MS) * dt;
          apply();
        }
      }
      raf = requestAnimationFrame(step);
    };
    const sync = () => {
      cancelAnimationFrame(raf);
      if (mqMobile.matches || mqReduce.matches) {
        track.style.transform = "";
      } else {
        last = performance.now();
        raf = requestAnimationFrame(step);
      }
    };
    sync();
    mqMobile.addEventListener("change", sync);
    mqReduce.addEventListener("change", sync);
    return () => {
      cancelAnimationFrame(raf);
      mqMobile.removeEventListener("change", sync);
      mqReduce.removeEventListener("change", sync);
    };
  }, [apply]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) {
      if (open) window.__lenis.stop();
      else window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return;
    const s = st.current;
    s.armed = true;
    s.dragging = false;
    s.moved = 0;
    s.lastX = e.clientX;
    s.pointerId = e.pointerId;
  };
  const onPointerMove = (e) => {
    const s = st.current;
    if (!s.armed && !s.dragging) return;
    const dx = e.clientX - s.lastX;
    s.lastX = e.clientX;
    s.moved += Math.abs(dx);
    if (!s.dragging && s.moved > 6) {
      s.dragging = true;
      viewportRef.current?.setPointerCapture(s.pointerId);
    }
    if (s.dragging) {
      s.offset += dx;
      apply();
    }
  };
  const endDrag = () => {
    st.current.armed = false;
    st.current.dragging = false;
  };
  const onClickCapture = (e) => {
    if (st.current.moved > 8) {
      e.preventDefault();
      e.stopPropagation();
      st.current.moved = 0;
    }
  };

  const ServiceCard = ({ s, clone = false }) => (
    <button
      type="button"
      {...(clone
        ? { tabIndex: -1, "aria-hidden": true }
        : { "data-testid": `service-card-${s.slug}` })}
      onClick={() => setOpen(s.slug)}
      className="group relative h-[400px] w-[270px] shrink-0 snap-start overflow-hidden rounded-[28px] text-left shadow-[0_4px_24px_rgba(16,32,46,0.08)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(16,32,46,0.18)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F7F3] sm:w-[310px] lg:h-[460px] lg:w-[350px]"
    >
      <img
        src={`/assets/services/${s.slug}.png`}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#10202E]/85 via-[#10202E]/20 to-transparent" />
      <div className="relative flex h-full flex-col justify-end p-7">
        <span
          className="absolute left-6 top-6 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#10202E]"
          style={{ background: s.tint }}
        >
          {s.category}
        </span>
        <h3 className="font-display text-3xl font-semibold leading-tight text-white">{s.title}</h3>
        <p className="mt-3 text-sm font-light leading-relaxed text-white/85 transition-[opacity,transform,max-height] duration-500 lg:max-h-0 lg:translate-y-3 lg:opacity-0 lg:group-hover:max-h-28 lg:group-hover:translate-y-0 lg:group-hover:opacity-100 lg:group-focus-visible:max-h-28 lg:group-focus-visible:translate-y-0 lg:group-focus-visible:opacity-100">
          {s.desc}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-300 transition-[opacity,transform] duration-500 lg:translate-y-2 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100 lg:group-focus-visible:translate-y-0 lg:group-focus-visible:opacity-100">
          Explore service
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-1" />
        </span>
      </div>
    </button>
  );

  return (
    <>
      <Reveal delay={0.1}>
        <div
          ref={viewportRef}
          data-testid="services-marquee"
          className="services-marquee mt-16 select-none lg:cursor-grab lg:active:cursor-grabbing"
          onPointerEnter={() => { st.current.hover = true; }}
          onPointerLeave={() => {
            const s = st.current;
            s.hover = false;
            if (!s.dragging) s.armed = false;
          }}
          onFocusCapture={() => { st.current.focus = true; }}
          onBlurCapture={() => { st.current.focus = false; }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onClickCapture}
        >
          <div ref={trackRef} className="services-track flex w-max gap-5 px-6 py-4">
            {services.map((s) => (
              <ServiceCard key={s.slug} s={s} />
            ))}
            {services.map((s) => (
              <ServiceCard key={`clone-${s.slug}`} s={s} clone />
            ))}
          </div>
        </div>
      </Reveal>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="service-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] overflow-y-auto bg-[#F8F7F3]/95 backdrop-blur-xl"
            onClick={() => setOpen(null)}
          >
            <div className="mx-auto max-w-2xl px-6 py-24">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                onClick={(e) => e.stopPropagation()}
                className="rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_24px_80px_rgba(16,32,46,0.12)] sm:p-12"
              >
                <div className="flex items-start justify-between gap-6">
                  <span
                    data-testid="service-modal-chip"
                    className="inline-block rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#10202E]"
                    style={{ background: active.tint }}
                  >
                    HRKAPITAL Services
                  </span>
                  <button
                    type="button"
                    data-testid="service-modal-close"
                    onClick={() => setOpen(null)}
                    className="rounded-full border border-[#10202E]/15 p-2 text-[#10202E] transition-colors hover:border-emerald-600/50 hover:text-emerald-700"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-8 flex items-end justify-between gap-6">
                  <h3 className="font-display text-3xl leading-tight text-[#10202E] sm:text-4xl">
                    {active.title}
                  </h3>
                  <div className="hidden shrink-0 sm:block">
                    <Art kind={active.art} />
                  </div>
                </div>
                <p className="mt-4 font-display text-xl italic text-emerald-700">{active.desc}</p>
                <div className="mt-6 space-y-5">
                  {active.body.map((p, i) => (
                    <p key={i} className="text-base font-light leading-relaxed text-[#33506A]">
                      {p}
                    </p>
                  ))}
                </div>
                <p className="mt-8 border-t border-[#10202E]/10 pt-6 font-mono text-[10px] leading-relaxed tracking-[0.12em] text-[#8AA0B0]">
                  {active.fine.toUpperCase()}
                </p>
                <button
                  type="button"
                  data-testid="service-modal-cta"
                  onClick={() => {
                    setOpen(null);
                    setTimeout(() => scrollToId("contact"), 300);
                  }}
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-[#04120c] transition-colors duration-300 hover:bg-emerald-300"
                >
                  Start a conversation
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative overflow-hidden py-28 md:py-40">
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <Reveal>
            <Eyebrow>03 — Services</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Everything starts with <span className="italic text-emerald-600">your goals.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-base font-light leading-relaxed text-[#4E6A82]">
            Nine ways we work together. One philosophy: clarity first, products second. Tap any
            service to learn more.
          </p>
        </Reveal>
      </div>
    </div>

    <ServiceMarquee />
  </section>
);
